import { supabase } from '../lib/supabase'
import type { WriteResult } from './projectWrites'
import type { CallSheetSections } from './callSheetSections'

/**
 * Writing the body of a call sheet — schedule, shots, kit, contacts, fields.
 *
 * Everything here is addressed by the call sheet's own id and goes through
 * SECURITY DEFINER functions, never a direct table write: the underlying
 * tables carry row-level security with no policies at all, so the functions
 * are the only door in, and they re-check the signed-in session's role server
 * side. Only the publishable key is ever used.
 *
 * Creating, listing and attaching a call sheet lives in utils/callSheets.ts.
 * This file is the body; that one is the identity.
 *
 * (The file and the database tables still say "shoot day" — historical, from
 * before 13 Sep 2026. The product calls this a call sheet everywhere a person
 * can see, and renaming four tables and a dozen functions would have been
 * risk without benefit.)
 */

function fail(fn: string, error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `${fn}: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] rpc refused — ${detail}`)
  return { ok: false, error: detail }
}

export interface ShootDayScheduleItem {
  id?: string
  timeLabel: string
  endTimeLabel: string
  title: string
  durationNote: string
  peopleNote: string
  locationNote: string
  note: string
}

export type ShotSectionName = 'Essential' | 'B-Roll' | 'Optional'

export interface ShootDayShotItem {
  id?: string
  section: ShotSectionName
  name: string
  cameraLabel: string
  referenceImageUrl: string
  checked?: boolean
}

export type KitSectionName = 'Camera' | 'Audio' | 'Lighting' | 'Media/Power'

export interface ShootDayKitItem {
  id?: string
  section: KitSectionName
  name: string
  quantity: string
  /** Who's bringing/responsible for it — useful when a freelancer supplies their own gear. */
  owner: string
  checked?: boolean
}

/**
 * A crew member as the PDF and other read-only consumers need them. The
 * richer shape the builder works with (which knows whether a person was
 * inherited from a project) is CallSheetCrewMember in utils/callSheets.ts.
 */
export interface ShootDayCrewMember {
  userId: string
  name: string
  role: string
  phone: string | null
}

/**
 * A person who isn't on the call sheet's own crew — an extra client-side
 * contact, a featured guest, or freelance/on-the-day crew with no Portal OS
 * account. All three share this shape; `kind` picks which of the three lists
 * a row belongs to.
 */
export type ShootDayContactKind = 'client' | 'guest' | 'crew'

export interface ShootDayContact {
  id?: string
  role: string
  name: string
  phone: string
  email: string
  /** Only meaningful for kind 'crew' — on-site vs. joining remotely. */
  remote?: boolean
}

export interface ShootDayFields {
  weather: string
  parkingNotes: string
  /** Free-text venue address — printed on the call sheet and shown to crew. */
  locationAddress: string
  /** A pasted Google Maps (or similar) link — used directly when present,
      instead of building a maps search query out of the address text. */
  locationMapUrl: string
  /** Merged 8 Sep 2026: this used to be two separate fields (Important
      Instructions + Client Notes). Both were already staff-only, so folding
      them into one changes nothing about who sees it. */
  importantInstructions: string
  scriptUrl: string
  guestBiosUrl: string
  storyboardUrl: string
  dropboxUrl: string
}

export async function saveShootDayFields(
  callSheetId: string,
  fields: ShootDayFields,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const { error } = await supabase.rpc('update_shoot_day_fields', {
    p_shoot_day_id: callSheetId,
    p_weather: fields.weather || null,
    p_parking_notes: fields.parkingNotes || null,
    // nearest_hospital is deliberately left null going forward — dropped
    // from the builder UI 8 Sep 2026 at Tom's request.
    p_nearest_hospital: null,
    p_location_address: fields.locationAddress || null,
    p_location_map_url: fields.locationMapUrl || null,
    p_important_instructions: fields.importantInstructions || null,
    // client_notes is deliberately left null going forward — Important
    // Instructions and Client Notes were merged into one field 8 Sep 2026.
    p_client_notes: null,
    p_script_url: fields.scriptUrl || null,
    p_guest_bios_url: fields.guestBiosUrl || null,
    p_storyboard_url: fields.storyboardUrl || null,
    p_dropbox_url: fields.dropboxUrl || null,
  })

  return fail('update_shoot_day_fields', error)
}

/**
 * Which optional segments this call sheet publishes.
 *
 * Its own RPC rather than three more parameters on update_shoot_day_fields:
 * adding defaulted parameters there would create a Postgres overload rather
 * than replace the function, and this codebase has already been bitten once
 * by an ambiguous overloaded RPC (see the insert_transcript_revision note in
 * the transcript worker). Separate also means the builder can save a switch
 * the moment it's clicked.
 */
export async function saveShootDaySections(
  callSheetId: string,
  sections: CallSheetSections,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const { error } = await supabase.rpc('set_shoot_day_sections', {
    p_shoot_day_id: callSheetId,
    p_show_shots: sections.shots,
    p_show_kit: sections.kit,
    p_show_files: sections.files,
  })

  return fail('set_shoot_day_sections', error)
}

/** Replaces the whole schedule in one call — the builder always saves the full list. */
export async function saveShootDaySchedule(
  callSheetId: string,
  items: ShootDayScheduleItem[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const { error } = await supabase.rpc('set_shoot_day_schedule', {
    p_shoot_day_id: callSheetId,
    p_items: items.map((item) => ({
      timeLabel: item.timeLabel,
      endTimeLabel: item.endTimeLabel,
      title: item.title,
      durationNote: item.durationNote,
      peopleNote: item.peopleNote,
      locationNote: item.locationNote,
      note: item.note,
    })),
  })

  return fail('set_shoot_day_schedule', error)
}

export async function saveShootDayShots(
  callSheetId: string,
  items: ShootDayShotItem[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const { error } = await supabase.rpc('set_shoot_day_shots', {
    p_shoot_day_id: callSheetId,
    p_items: items.map((item) => ({
      section: item.section,
      name: item.name,
      cameraLabel: item.cameraLabel,
      referenceImageUrl: item.referenceImageUrl,
    })),
  })

  return fail('set_shoot_day_shots', error)
}

export async function saveShootDayKit(
  callSheetId: string,
  items: ShootDayKitItem[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const { error } = await supabase.rpc('set_shoot_day_kit', {
    p_shoot_day_id: callSheetId,
    p_items: items.map((item) => ({
      section: item.section,
      name: item.name,
      quantity: item.quantity,
      owner: item.owner,
    })),
  })

  return fail('set_shoot_day_kit', error)
}

/**
 * Replaces one of the three extra-people lists (client contacts, featured
 * guests, or extra/freelance crew) in one call, same replace-the-whole-list
 * pattern as schedule/shots/kit above.
 */
export async function saveShootDayContacts(
  callSheetId: string,
  kind: ShootDayContactKind,
  items: ShootDayContact[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const { error } = await supabase.rpc('set_shoot_day_contacts', {
    p_shoot_day_id: callSheetId,
    p_kind: kind,
    p_items: items.map((item) => ({
      role: item.role,
      name: item.name,
      phone: item.phone,
      email: item.email,
      remote: kind === 'crew' ? Boolean(item.remote) : undefined,
    })),
  })

  return fail('set_shoot_day_contacts', error)
}

/** Saved once against the crew member's own record, so every future call
    sheet reuses it. */
export async function setStaffPhone(userId: string, phone: string): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase.rpc('set_staff_phone', { p_user_id: userId, p_phone: phone })
  return fail('set_staff_phone', error)
}

/** Saved on the project itself, so it carries over to every call sheet
    attached to that project. Only meaningful for an attached call sheet — a
    standalone one keeps its client contacts in shoot_day_contacts instead. */
export async function setClientContactPhone(projectId: string, phone: string): Promise<WriteResult> {
  if (!supabase || !projectId) return { ok: true }

  const { error } = await supabase.rpc('set_project_client_contact_phone', {
    p_project_id: projectId,
    p_phone: phone,
  })
  return fail('set_project_client_contact_phone', error)
}
